import { z } from "zod";

import { agaveEvalCostAttributionSchema } from "../agave-evals/schemas";

export const reviewSearchSchema = z.object({
  runId: z.string().min(1).max(512).optional(),
  attemptId: z.string().min(1).max(512).optional(),
});
export type ReviewSearch = z.infer<typeof reviewSearchSchema>;

export const feedbackSchema = z.object({
  rating: z.number().int().min(1).max(5).nullable(),
  text: z.string().nullable(),
  timestampSeconds: z.number().finite().nonnegative().nullable(),
  submittedAt: z.string(),
});

export const feedbackSubmissionSchema = z.union([
  feedbackSchema.omit({ submittedAt: true }).extend({
    rating: z.null(),
    submissionId: z.string().uuid().optional(),
    text: z.string().trim().min(1).max(10000),
  }),
  feedbackSchema.omit({ submittedAt: true }).extend({
    rating: z.number().int().min(1).max(5),
    submissionId: z.string().uuid().optional(),
    text: z.null(),
    timestampSeconds: z.null(),
  }),
]);

export const reviewPlaybackSchema = z.object({
  outputUrl: z.string().url().nullable(),
  sourceUrls: z.array(z.object({ assetId: z.string(), url: z.string().url() })),
  expiresAt: z.string().nullable(),
});

export const reviewRunSchema = z.object({
  runId: z.string().min(1),
  displayName: z.string().nullable(),
  createdAt: z.string(),
  status: z.string(),
  gitSha: z.string().nullable(),
  backend: z.string().nullable(),
  caseSetId: z.string().nullable().default(null),
});

export const reviewAttemptSchema = z.object({
  attemptId: z.string().min(1),
  runId: z.string().min(1),
  caseName: z.string(),
  trialIndex: z.number().int().nonnegative(),
  styleId: z.string().nullable(),
  styleName: z.string().nullable(),
  aspectRatio: z.string().nullable(),
  prompt: z.string().nullable(),
  sourceAssetIds: z.array(z.string()),
  enableBroll: z.boolean().nullable().default(null),
  backend: z.string().nullable(),
  status: z.enum(["succeeded", "failed"]),
  error: z.string().nullable(),
  videoAssetId: z.string().nullable(),
  projectDataUri: z.string().nullable(),
  workflowId: z.string().nullable(),
  gitSha: z.string().nullable(),
  costAttribution: agaveEvalCostAttributionSchema.nullable().default(null),
  creditBreakdown: z.record(z.unknown()).nullable().default(null),
  credits: z.number().finite().nonnegative().nullable(),
  latencySeconds: z.number().finite().nonnegative().nullable(),
  feedback: feedbackSchema.nullable(),
  notes: z.array(feedbackSchema).default([]),
});

export const reviewAttemptsResponseSchema = z.object({
  attempts: z.array(reviewAttemptSchema),
  notesSupported: z.boolean().default(false),
  editorImportSupported: z.boolean().default(false),
});

export type ReviewAttempt = z.infer<typeof reviewAttemptSchema>;
export type ReviewRun = z.infer<typeof reviewRunSchema>;
export type ReviewFeedback = z.infer<typeof feedbackSchema>;
export type ReviewPlayback = z.infer<typeof reviewPlaybackSchema>;
export type FeedbackSubmission = z.input<typeof feedbackSubmissionSchema>;
